import { localDateString } from '../../../../utils/helpers/localDate';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    getVmsActivityLogs,
    exportVmsActivityLogs,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import { notify } from '../../../../utils/notification';
import useVmsList from '../useVmsList';
import VmsListShell from '../components/vms/VmsListShell';
import VmsTable from '../components/vms/VmsTable';
import { labelCls } from '../components/form/formStyles';
import { buildActivityCsv, downloadCsv, EXPORT_ROW_CAP } from './activityCsv';
import {
    ACTIVITY_TYPES,
    ACTIVITY_ICON,
    ACTIVITY_TONE,
    fmtDateTime,
    humanize,
    personName,
    toOptions,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const TYPE_OPTIONS = toOptions(ACTIVITY_TYPES);

const ActivityLogs = () => {
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const canExport = can(PERMISSIONS.VMS_VISITOR_LOG_EXPORT);

    const exportLoading = useSelector(state => state.VmsReducer?.exportLoading);

    const list = useVmsList({
        slice: 'activityLogs',
        action: getVmsActivityLogs,
        filters: { activityType: '', fromDate: '', toDate: '' },
        limit: 20,
    });

    const handleExport = () => {
        dispatch(exportVmsActivityLogs({
            societyId: list.societyId,
            // The export endpoint takes no activity_type filter, so only the
            // dates carry over — the button label says as much.
            fromDate: list.filters.fromDate || undefined,
            toDate: list.filters.toDate || undefined,
            onSuccess: (res) => {
                const rows = Array.isArray(res?.data) ? res.data : [];
                if (rows.length === 0) {
                    notify('warning', 'Nothing to export for this date range');
                    return;
                }
                const stamp = localDateString();
                downloadCsv(`visitor-activity-${stamp}.csv`, buildActivityCsv(rows));
                // The server truncates at 10 000 with no flag, so a full page
                // is indistinguishable from a complete one — say so rather than
                // let someone file an incomplete audit export.
                if (rows.length >= EXPORT_ROW_CAP) {
                    notify('warning', `Export capped at ${EXPORT_ROW_CAP.toLocaleString()} rows — narrow the date range to get the rest`);
                } else {
                    notify('success', `Exported ${rows.length.toLocaleString()} rows`);
                }
            },
        }));
    };

    const columns = [
        {
            key: 'created_at',
            header: 'When',
            nowrap: true,
            render: (r) => <span className="tw-text-[11px]">{fmtDateTime(r.created_at)}</span>,
        },
        {
            key: 'activity_type',
            header: 'Activity',
            nowrap: true,
            render: (r) => (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                    <span className={`tw-w-6 tw-h-6 tw-rounded tw-flex tw-items-center tw-justify-center tw-shrink-0 ${ACTIVITY_TONE[r.activity_type] || 'tw-bg-sky-50 tw-text-sky-600'}`}>
                        <Icon icon={ACTIVITY_ICON[r.activity_type] || 'mdi:circle-small'} className="tw-text-sm" />
                    </span>
                    <span className="tw-font-semibold tw-text-gray-800">{humanize(r.activity_type)}</span>
                </span>
            ),
        },
        {
            key: 'description',
            header: 'Detail',
            render: (r) => <span className="tw-text-gray-700">{r.description || '—'}</span>,
        },
        {
            key: 'visitor',
            header: 'Visitor',
            render: (r) => (
                r.visitor
                    ? (
                        <div className="tw-min-w-0">
                            <p className="tw-mb-0 tw-truncate tw-text-gray-800">{r.visitor.name}</p>
                            <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-font-mono">
                                {r.visitor.phone ? fmtPhone(r.visitor.phone) : ''}
                            </p>
                        </div>
                    )
                    : <span className="tw-text-gray-300">—</span>
            ),
        },
        {
            key: 'visit',
            header: 'Visit',
            nowrap: true,
            render: (r) => r.visit
                ? <span className="tw-font-mono tw-text-[10px]">{r.visit.visit_code}</span>
                : <span className="tw-text-gray-300">—</span>,
        },
        {
            key: 'actor',
            header: 'By',
            render: (r) => (r.actor ? personName(r.actor) : <span className="tw-text-gray-300">System</span>),
        },
    ];

    const filters = (
        <>
            <div>
                <label className={labelCls}>Activity</label>
                <SearchableDropdown
                    small
                    value={list.filters.activityType}
                    onChange={(v) => list.setFilter('activityType', v === 'all' ? '' : v)}
                    options={TYPE_OPTIONS}
                    placeholder="All activity"
                />
            </div>
            <div>
                <label className={labelCls}>From</label>
                <input
                    type="date"
                    value={list.filters.fromDate}
                    onChange={e => list.setFilter('fromDate', e.target.value)}
                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-border-sky-500"
                />
            </div>
            <div>
                <label className={labelCls}>To</label>
                <input
                    type="date"
                    value={list.filters.toDate}
                    onChange={e => list.setFilter('toDate', e.target.value)}
                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-border-sky-500"
                />
            </div>
            <div className="tw-flex tw-items-end">
                <button
                    type="button"
                    onClick={list.resetFilters}
                    disabled={!list.hasFilters}
                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-300 tw-bg-white tw-rounded hover:tw-bg-gray-50 disabled:tw-opacity-40"
                >
                    <Icon icon="mdi:filter-remove-outline" className="tw-text-base" />
                    Clear
                </button>
            </div>
        </>
    );

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <VmsListShell
                title="Visitor Activity"
                subtitle="Append-only audit trail written by the visitor, visit, pass and gate-log modules"
                itemLabel="entries"
                list={list}
                filters={filters}
                headerActions={canExport && (
                    <button
                        onClick={handleExport}
                        disabled={exportLoading}
                        title="Exports the current date range — the activity filter does not apply"
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-50"
                    >
                        <Icon icon="mdi:file-delimited-outline" className="tw-text-base" />
                        {exportLoading ? 'Preparing...' : 'Export CSV'}
                    </button>
                )}
            >
                <VmsTable
                    columns={columns}
                    rows={list.rows}
                    loading={list.loading}
                    loaderLabel="Loading activity..."
                    rowKey={(r) => r.activity_id}
                    empty={{
                        icon: 'mdi:history',
                        filtered: list.hasFilters,
                        filteredTitle: 'No activity matches these filters',
                        emptyTitle: 'No activity recorded yet',
                        emptyHint: 'Entries appear here automatically as visitors are registered, visits move through approval, and passes are issued or revoked.',
                        onClear: list.resetFilters,
                    }}
                />
            </VmsListShell>

            {/* Two activity types exist in the backend enum but are never
                written, so they are left out of the filter rather than offered
                as choices that always return nothing. */}
            <p className="tw-mb-0 tw-text-[10px] tw-text-gray-400 tw-px-1">
                A pre-approval or gate pass being consumed is not logged separately — it is recorded on the
                <strong> Visit created</strong> entry for that visit.
            </p>
        </div>
    );
};

export default ActivityLogs;
